import { options } from "@common/consts/options";
import type { Todo } from "@common/types/todo";
import type { TimerStoreSyncPayload } from "@common/use-cases/timer/timer-network-sync";
import EventEmitter from "eventemitter3";
import type { Socket } from "socket.io-client";
import { Manager } from "socket.io-client";

export type SessionState = {
  lastUpdateUtc: number;
  timer: TimerStoreSyncPayload | null;
  currentTodo: Todo | null;
};

export const SESSION_DEFAULT_STATE: SessionState = {
  lastUpdateUtc: 0,
  timer: null,
  currentTodo: null,
};

interface RealtimeServiceDependencies {
  getAccessToken: () => string | null;
}

export type ServerToClientEvents = {
  "session.connected": () => void;
  "session.updated": (data: SessionState) => void;
  "session.patched": (data: SessionState, delta: Partial<SessionState>) => void;
};

export type ClientToServerEvents = {
  "session.update": (data: SessionState) => void;
  "session.patch": (delta: Partial<SessionState>) => void;
};

export function makeRealtimeService(deps: RealtimeServiceDependencies) {
  let session = {
    lastUpdateUtc: 0,
  };
  let socket: Socket<ServerToClientEvents, ClientToServerEvents> | undefined;

  const realtimeService = {
    eventEmitter: new EventEmitter<{
      ["session.updated"]: ServerToClientEvents["session.updated"];
      ["session.patched"]: ServerToClientEvents["session.patched"];
    }>(),
    connect: () => {
      console.log(`** Realtime: connecting to ${options.realtimeServerApi}...`);

      if (!deps.getAccessToken()) throw new Error("No access token");

      const manager = new Manager<ServerToClientEvents, ClientToServerEvents>(
        options.realtimeServerApi
      );
      socket = manager.socket("/", {
        auth: {
          token: deps.getAccessToken(),
        },
      });

      // socket.on("connect", () => console.log("socket connect"));
      socket.on("session.connected", () => {
        console.log("** Realtime: connected");
      });

      socket.on("session.updated", (sessionData) => {
        console.log("** Realtime: session updated", sessionData);
        realtimeService.eventEmitter.emit("session.updated", sessionData);
        if (sessionData.lastUpdateUtc > session.lastUpdateUtc) {
          // getCurrentStore()?.loadTodos();
          console.log("TODO: trigger reload!");
        }
      });

      socket.on("session.patched", (sessionData, delta) => {
        console.log("** Realtime: session patched", sessionData);
        realtimeService.eventEmitter.emit(
          "session.patched",
          sessionData,
          delta
        );
        if (sessionData.lastUpdateUtc > session.lastUpdateUtc) {
          // getCurrentStore()?.loadTodos();
          console.log("TODO: trigger reload!");
        }
      });
    },
    cleanup: () => {
      socket?.disconnect();
    },
    updateState: (state: Omit<SessionState, "lastUpdateUtc">) => {
      if (!socket) {
        console.error("Realtime not connected");
        return;
      }

      socket?.emit("session.update", { ...state, lastUpdateUtc: Date.now() });
    },
    patchState: (delta: Partial<Omit<SessionState, "lastUpdateUtc">>) => {
      if (!socket) {
        console.error("Realtime not connected");
        return;
      }

      socket?.emit("session.patch", delta);
    },
  };

  return realtimeService;
}
export type RealtimeService = ReturnType<typeof makeRealtimeService>;
